'use client';

import { useTranslations } from 'next-intl';
import { createContext, useCallback, useContext, useState } from 'react';

import { ResponsiveModal } from '@ui/components/features/selection/responsiveModal';
import { Button } from '@ui/components/ui/button';
import { type RateSessionData, RateSessionDrawer } from '@website/components/RateSessionDrawer';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { getReviewsByActivityId } from '@website/services/reviews.service';

interface OpenRateSessionOptions {
  skipCheck?: boolean;
}

interface RateSessionContextValue {
  openRateSession: (data: RateSessionData, options?: OpenRateSessionOptions) => void;
  closeRateSession: () => void;
  isOpen: boolean;
}

const RateSessionContext = createContext<RateSessionContextValue | null>(null);

export function useRateSession(): RateSessionContextValue {
  const ctx = useContext(RateSessionContext);
  if (!ctx) {
    throw new Error('useRateSession must be used within a RateSessionProvider');
  }
  return ctx;
}

export function RateSessionProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations('RateSession');
  const { data: user } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [sessionData, setSessionData] = useState<RateSessionData | null>(null);

  const openRateSession = useCallback(
    async (data: RateSessionData, options?: OpenRateSessionOptions) => {
      if (!options?.skipCheck && user?.id && data.activityId) {
        try {
          const result = await getReviewsByActivityId(data.activityId, { limit: 100 });
          if (result.reviews.some((r) => r.userId === user.id)) {
            setSessionData(data);
            setAlreadySubmitted(true);
            setIsOpen(true);
            return;
          }
        } catch {
          // If check fails, proceed with normal flow
        }
      }
      setSessionData(data);
      setAlreadySubmitted(false);
      setIsOpen(true);
    },
    [user]
  );

  const closeRateSession = useCallback(() => {
    setIsOpen(false);
    setAlreadySubmitted(false);
    setSessionData(null);
  }, []);

  return (
    <RateSessionContext.Provider value={{ openRateSession, closeRateSession, isOpen }}>
      {children}
      {alreadySubmitted ? (
        <AlreadySubmittedDialog isOpen={isOpen} onClose={closeRateSession} t={t} />
      ) : (
        <RateSessionDrawer isOpen={isOpen} onClose={closeRateSession} sessionData={sessionData} />
      )}
    </RateSessionContext.Provider>
  );
}

function AlreadySubmittedDialog({
  isOpen,
  onClose,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  t: ReturnType<typeof useTranslations<'RateSession'>>;
}) {
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('alreadySubmittedTitle')}
      showCloseButton
      drawerClassName="p-0 rounded-t-xl"
      dialogClassName="sm:max-w-sm p-0"
    >
      <div className="flex flex-col gap-6 items-center px-5 pb-6 pt-4 md:px-8 md:py-8">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 md:w-10 md:h-10 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div className="text-center">
          <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">{t('alreadySubmittedTitle')}</h3>
          <p className="text-sm md:text-base text-neutral-500 mt-2">{t('alreadySubmittedMessage')}</p>
        </div>

        <Button onClick={onClose} className="w-full md:max-w-xs">
          {t('done')}
        </Button>
      </div>
    </ResponsiveModal>
  );
}
